import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { pwaCacheManager } from "@/utils/pwaCacheManager";
import { useAuth } from "@/contexts/AuthContext";

export function usePWACache() {
  const location = useLocation();
  const { user } = useAuth();

  // Cache current page
  const cachePage = useCallback(
    async (url: string, priority: "high" | "medium" | "low" = "medium") => {
      try {
        // Get page title from document
        const title = document.title || "PayStreem";

        // Get page content (simplified - in real implementation you'd want to serialize the React component state)
        const content = document.documentElement.outerHTML;

        await pwaCacheManager.cachePage(url, content, title, priority);
      } catch (error) {
        console.warn("Failed to cache page:", error);
      }
    },
    [],
  );

  // Cache user-specific content when user logs in
  const cacheUserContent = useCallback(async () => {
    if (user?.id) {
      try {
        await pwaCacheManager.cacheUserContent(user.id);
        console.log("✅ User content cached for offline access");
      } catch (error) {
        console.warn("⚠️ Failed to cache user content:", error);
      }
    }
  }, [user?.id]);

  // Pre-cache critical pages based on user role
  const preCacheCriticalPages = useCallback(async () => {
    if (user) {
      try {
        await pwaCacheManager.preCacheCriticalPages(user.role);
        console.log("✅ Critical pages pre-cached");
      } catch (error) {
        console.warn("⚠️ Failed to pre-cache critical pages:", error);
      }
    }
  }, [user]);

  // Auto-cache current page when user navigates
  useEffect(() => {
    const timer = setTimeout(() => {
      const currentPath = location.pathname;

      // Determine priority based on page type
      let priority: "high" | "medium" | "low" = "medium";

      if (
        ["/", "/dashboard", "/profile", "/tracks", "/favorites"].includes(
          currentPath,
        )
      ) {
        priority = "high";
      } else if (currentPath.startsWith("/dashboard/")) {
        priority = "high";
      } else if (
        ["/about", "/faq", "/artists", "/charts"].includes(currentPath)
      ) {
        priority = "low";
      }

      cachePage(currentPath, priority);
    }, 2000); // Cache after 2 seconds to ensure page is fully loaded

    return () => clearTimeout(timer);
  }, [location.pathname, cachePage]);

  // Cache user content when user logs in
  useEffect(() => {
    if (user) {
      const timer = setTimeout(() => {
        cacheUserContent();
        preCacheCriticalPages();
      }, 3000); // Cache user content after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [user, cacheUserContent, preCacheCriticalPages]);

  // Listen for service worker cache updates
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "CACHE_UPDATED") {
        console.log("✅ Cache updated via background sync");
        // You could dispatch a custom event or update some state here
        window.dispatchEvent(
          new CustomEvent("cacheUpdated", {
            detail: { timestamp: event.data.timestamp },
          }),
        );
      }
    };

    navigator.serviceWorker?.addEventListener("message", handleMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener("message", handleMessage);
    };
  }, []);

  return {
    cachePage,
    cacheUserContent,
    preCacheCriticalPages,
    pwaCacheManager,
  };
}
